/**
 * Where to land after switching organization.
 *
 * The switch used to reload the current URL, so a user on
 * /dashboards/<id> or /metrics/<id> asked the new organization for the old
 * one's item and got "No Dashboard matches the given query" - and Retry and
 * refresh asked again for the same id (Sentry user feedback 39 / 3B). The
 * page after a switch is the list for the section the user was in, which
 * exists in every organization.
 */

import { tenantSubdomainFromHost, workspaceHostUrl } from './hostTenant'
import { TENANT_DOMAIN_BASE } from './subdomain'

/** Sections whose first path segment is also a list route. */
const LIST_SECTIONS = new Set([
  'categories',
  'dashboards',
  'data-entry',
  'metrics',
  'partitions',
  'report-templates',
  'reports',
  'settings',
])

const HOME_PATH = '/'

/**
 * @param {string} pathname - the current location.pathname
 * @returns {string} the path to load in the new organization
 */
export function pathAfterTenantSwitch(pathname) {
  const section = (pathname || '').split('/').filter(Boolean)[0]
  return LIST_SECTIONS.has(section) ? `/${section}` : HOME_PATH
}

/**
 * A full page load into the new organization (the switch itself is already
 * saved), so nothing cached for the old one survives.
 */
export function reloadIntoSwitchedTenant() {
  window.location.assign(pathAfterTenantSwitch(window.location.pathname))
}

/**
 * Where another workspace opens when the user is on a workspace host.
 *
 * On acme.graphit.io the host picks the workspace (useHostTenant), so
 * switching in place would be undone on the next load: the other workspace
 * opens on its own host instead, on the same section's list. Sessions are
 * per host, so the user may have to sign in there. On the app host (and
 * anywhere else) this is null and switching stays in place.
 *
 * @param {{subdomain?: string}} tenant - the workspace being switched to
 * @param {{hostname: string, pathname: string, protocol: string, port: string}} [location]
 * @param {string} [base] - the tenant domain base
 * @returns {{address: string, url: string}|null}
 */
export function workspaceHostTarget(tenant, location = window.location, base = TENANT_DOMAIN_BASE) {
  if (!tenantSubdomainFromHost(location.hostname, base)) return null
  const subdomain = String(tenant?.subdomain || '').toLowerCase()
  const url = workspaceHostUrl(subdomain, pathAfterTenantSwitch(location.pathname), {
    protocol: location.protocol,
    port: location.port,
    base,
  })
  return url ? { address: `${subdomain}.${base}`, url } : null
}

/**
 * The switchers' move to another workspace: its own host when on a workspace
 * host, otherwise an in-place switch followed by a full load of the new
 * organization.
 *
 * @param {{subdomain?: string}} tenant
 * @param {(tenant: Object) => Promise<unknown>} switchInPlace - saves the switch
 *   (the switchTenant thunk, unwrapped); rejects when it fails
 * @returns {Promise<void>}
 */
export async function openWorkspace(tenant, switchInPlace) {
  const hostTarget = workspaceHostTarget(tenant)
  if (hostTarget) {
    window.location.assign(hostTarget.url)
    return
  }
  await switchInPlace(tenant)
  reloadIntoSwitchedTenant()
}
